import React, { memo, useEffect, useRef, useState } from "react";
import * as echarts from "echarts";
import { Checkbox, Divider, Empty, Flex } from "antd";
import download from "../../assets/Icons/download.svg";
import refresh from "../../assets/Icons/refresh.svg";
import graph from "../../assets/Icons/graphIcon.svg";
import graphStats from "../../assets/Icons/graphStatsIcon.svg";
import notes from "../../assets/Icons/notesIcon.svg";
import "../CurveGraph/CurveGraph.scss";
import { formatCurrency, formatWithCommas } from "../../assets/helpers";
import { useSelector } from "react-redux";

const BankBarChart = ({
  categories = [],
  seriesData = [],
  barColors = [],
  height = 250,
}) => {
  const chartRef = useRef(null);
  const [visibleSeries, setVisibleSeries] = useState([]);
  const { format } = useSelector(state => state.priceFormat)
  const { user } = useSelector(state => state.auth) 

  const [dynamicBarWidth, setDynamicBarWidth] = useState(16);

  const calculateBarWidth = () => {
    const width = window.innerWidth;
    if (width < 480) return 6;
    if (width < 768) return 10;
    return 16;
  };
  useEffect(() => {
    const updateBarWidth = () => {
      setDynamicBarWidth(calculateBarWidth());
    };

    updateBarWidth();
    window.addEventListener("resize", updateBarWidth);

    return () => window.removeEventListener("resize", updateBarWidth);
  }, []);

  useEffect(() => {
    if (seriesData.length) {
      setVisibleSeries(seriesData.map(() => true));
    }
  }, [seriesData]);

  const hasData =
    Array.isArray(seriesData) &&
    seriesData.length > 0 &&
    seriesData.some((s) => s.data?.some((val) => val != null));

  const toggleSeries = (index) => {
    setVisibleSeries((prev) => {
      const updated = [...prev];
      updated[index] = !updated[index];
      return updated;
    });
  };

  useEffect(() => {
    if (!chartRef.current || !visibleSeries.length) return;

    const chart = echarts.init(chartRef.current);

    const fullSeries = seriesData.map((series, index) => ({
      ...series,
      type: "bar",
      barWidth: dynamicBarWidth,
      itemStyle: {
        color: barColors[index] || "#ccc",
      },
    }));

    // Legend selected state
    const legendSelected = {};
    seriesData.forEach((s, idx) => {
      legendSelected[s.name] = !!visibleSeries[idx];
    });

    const option = {
      grid: {
        left: "3%",
        right: "3%",
        bottom: "10%",
        containLabel: true,
      },
      legend: {
        show: false, // we use custom legend below chart
        selected: legendSelected,
      },
      tooltip: {
        trigger: "item",
        backgroundColor: "transparent",
        extraCssText: `
         border: none !important;
         background: transparent !important;
         box-shadow: none !important;
         padding: 0 !important;
  `,
        formatter: function (params) {
          const { seriesName, value, color } = params;

          return `
      <div class="custom-tooltip-bar">
        <div class="header">
          <span class="square" style="background-color: ${color};"></span>
          <span class="label">${seriesName}</span>
        </div>
        <div class="value">
          ₹ ${formatWithCommas(value)}
 <span class="percent">(45%)</span>
        </div>
      </div>
    `;
        },
      },
      xAxis: {
        type: "category",
        data: categories,
        axisLabel: {
          color: "rgba(33, 35, 48, 0.5)",
          fontFamily: "Gilroy",
          fontSize: 10,
          fontWeight: 600,
        },
        axisLine: {
          lineStyle: { color: "#e0e0e0" },
        },
      },
      yAxis: {
        type: "log",
        axisLabel: {
          color: "rgba(33, 35, 48, 0.5)",
          fontFamily: "Gilroy",
          fontSize: 10,
          fontWeight: 600,
          formatter: (val) => formatWithCommas(val),
        },
        axisLine: {
          lineStyle: { color: "#e0e0e0" },
        },
        splitLine: {
          lineStyle: { color: "#eee" },
        },
      },
      series: fullSeries,
    };

    chart.setOption(option);

    const handleResize = () => chart.resize();
    window.addEventListener("resize", handleResize);

    return () => {
      chart.dispose();
      window.removeEventListener("resize", handleResize);
    };
  }, [seriesData, categories, visibleSeries, barColors, dynamicBarWidth]);

  return (
    <div className="curve-graph-wrapper" style={{
      width:user?.is_tc ? '100%' : '55%'
    }}>
      <div className="chart-toolbar">
        <p className="chart-header-name">ODPOS (INR)</p>

        <Flex className="icon-wrapper">
          <img src={notes} alt="notes" />
          <img src={graph} alt="graph" />
          <img src={graphStats} alt="graphStats" />
          <img src={refresh} alt="refresh" />
          <img src={download} alt="download" />
        </Flex>
      </div>

      {hasData ? (
        <div
          ref={chartRef}
          style={{ width: "100%", height }}
          className="echarts-container"
        />
      ) : (
        <div className="no-data-message" style={{ height }}>
          <Empty description={<span>No Data Available</span>} />
        </div>
      )}

      <Flex className="legend-container">
        <Flex className="legend-wrapper" align="center">
          {seriesData.map((s, index) => (
            <Flex className="legend" key={index} align="center">
              <Checkbox
                checked={visibleSeries[index]}
                onChange={() => toggleSeries(index)}
                className="legend-checkbox"
              >
                <Flex align="center" gap={6}>
                  <div
                    className={`color-div ${index === 0 ? "green" : "orange"}`}
                  ></div>
                  <span className="legend-name">{s.name}</span>
                </Flex>
              </Checkbox>

              {index === 0 && (
                <Divider
                  type="vertical"
                  style={{
                    height: 18,
                    marginInline: 16,
                    borderColor: "#7E808D",
                  }}
                />
              )}
            </Flex>
          ))}
        </Flex>
      </Flex>
    </div>
  );
};

export default memo(BankBarChart);
